import apiClient, { getApiOrigin } from './api';

function isSignedMediaUrl(url) {
  return /[?&]X-Amz-Signature=/i.test(url) || /[?&]Signature=/i.test(url);
}

/** Resolve logo_url to an absolute URL so img src works when frontend and API are on different origins. */
export function getLogoDisplayUrl(logoUrl, cacheKey) {
  if (!logoUrl) return null;
  let url = logoUrl;
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
    const origin = getApiOrigin();
    url = url.startsWith('/') ? `${origin}${url}` : `${origin}/${url}`;
  }
  if (cacheKey && !isSignedMediaUrl(url)) {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}v=${encodeURIComponent(String(cacheKey))}`;
  }
  return url;
}

/** Load company branding for the signed-in user's organization. */
export async function fetchOrganizationBranding() {
  const rebrandResponse = await apiClient.get('/settings/rebrand/', { skipCache: true });
  const rebrand = rebrandResponse?.data || {};
  if (rebrand.logo_url) return rebrand;

  try {
    const orgResponse = await apiClient.get('/settings/organization/', { skipCache: true });
    const org = Array.isArray(orgResponse?.data) ? orgResponse.data[0] : orgResponse?.data;
    if (!org) return rebrand;
    return {
      company_name: rebrand.company_name || org.organization_name || '',
      logo_url: rebrand.logo_url || org.logo || null,
      updated_at: rebrand.updated_at || org.updated_at || null,
    };
  } catch (_) {
    return rebrand;
  }
}

const settingsService = {
  // Get organization settings (singleton; list returns one-item array)
  getOrganizationSettings: async () => {
    const response = await apiClient.get('/settings/organization/');
    return response.data;
  },

  // Update organization settings (singleton current endpoint)
  updateOrganizationSettings: async (settings) => {
    const response = await apiClient.put('/settings/organization/current/', settings);
    return response.data;
  },

  // Get tracking settings (singleton)
  getTrackingSettings: async () => {
    const response = await apiClient.get('/settings/tracking/');
    return response.data;
  },
  getTrackSettings: async () => {
    return settingsService.getTrackingSettings();
  },

  // Update tracking settings (singleton current endpoint)
  updateTrackingSettings: async (settings) => {
    const response = await apiClient.put('/settings/tracking/current/', settings);
    return response.data;
  },
  updateTrackSettings: async (settings) => {
    return settingsService.updateTrackingSettings(settings);
  },

  // Idle excluded apps: apps that don't count toward idle when in foreground
  getIdleExcludedApps: async () => {
    const response = await apiClient.get('/settings/tracking/');
    const data = response?.data ?? response;
    const raw = Array.isArray(data) ? data[0] : data;
    return Array.isArray(raw?.idle_excluded_apps) ? raw.idle_excluded_apps : [];
  },
  addIdleExcludedApp: async (appName) => {
    const current = await settingsService.getIdleExcludedApps();
    const app = (appName || '').trim();
    if (!app || current.includes(app)) return current;
    const updated = [...current, app];
    await apiClient.put('/settings/tracking/current/', { idle_excluded_apps: updated });
    return updated;
  },
  removeIdleExcludedApp: async (appName) => {
    const current = await settingsService.getIdleExcludedApps();
    const updated = current.filter((a) => a !== appName);
    await apiClient.put('/settings/tracking/current/', { idle_excluded_apps: updated });
    return updated;
  },

  // Get leave settings
  getLeaveSettings: async () => {
    const response = await apiClient.get('/settings/leave/');
    return response.data;
  },

  // Update leave settings
  updateLeaveSettings: async (settings) => {
    const response = await apiClient.put('/settings/leave/current/', settings);
    return response.data;
  },

  // Get email report settings (optional; scheduled reports use /api/scheduled-reports/)
  getEmailReportSettings: async () => {
    const response = await apiClient.get('/settings/email-reports/');
    return response.data;
  },

  // Update email report settings
  updateEmailReportSettings: async (settings) => {
    const response = await apiClient.put('/settings/email-reports/', settings);
    return response.data;
  },

  // Email notifications & Mailchimp status (no sensitive data)
  getEmailNotificationsStatus: async () => {
    const response = await apiClient.get('/settings/email-notifications/');
    return response.data;
  },

  // Send a test email to verify SMTP config
  sendTestEmail: async (to) => {
    const response = await apiClient.post('/notifications/test-email/', { to });
    return response.data;
  },

  // Get rebrand settings (alias for organization: company_name, logo_url)
  getRebrandSettings: async () => fetchOrganizationBranding(),

  // Update rebrand settings (JSON: company_name, optional logo_base64)
  updateRebrandSettings: async (settings) => {
    const response = await apiClient.put('/settings/rebrand/current/', settings);
    return response.data;
  },

  // Update rebrand settings with logo file (multipart/form-data; use when uploading logo file for S3)
  updateRebrandSettingsFormData: async (formData) => {
    const headers = { ...apiClient.defaults.headers };
    delete headers['Content-Type'];
    const response = await apiClient.put('/settings/rebrand/current/', formData, { headers });
    return response.data;
  },

  // Get teams (from employees app)
  getTeams: async () => {
    if (process.env.NODE_ENV === 'test') {
      return [];
    }
    const response = await apiClient.get('/teams/');
    return response.data;
  },

  // Create team
  createTeam: async (teamData) => {
    const response = await apiClient.post('/teams/', teamData);
    return response.data;
  },

  // Update team
  updateTeam: async (id, teamData) => {
    const response = await apiClient.put(`/teams/${id}/`, teamData);
    return response.data;
  },

  // Delete team
  deleteTeam: async (id) => {
    await apiClient.delete(`/teams/${id}/`);
  },

  // Leave types CRUD (from leaves app)
  getLeaveTypes: async () => {
    const response = await apiClient.get('/leave-types/');
    return response.data;
  },
  createLeaveType: async (data) => {
    const response = await apiClient.post('/leave-types/', data);
    return response.data;
  },
  updateLeaveType: async (id, data) => {
    const response = await apiClient.patch(`/leave-types/${id}/`, data);
    return response.data;
  },
  deleteLeaveType: async (id) => {
    await apiClient.delete(`/leave-types/${id}/`);
  },

  // Alert triggers (risk user rules)
  getAlertTriggers: async (params = {}) => {
    const response = await apiClient.get('/settings/alert-triggers/', { params });
    return response.data;
  },
  createAlertTrigger: async (data) => {
    const response = await apiClient.post('/settings/alert-triggers/', data);
    return response.data;
  },
  updateAlertTrigger: async (id, data) => {
    const response = await apiClient.put(`/settings/alert-triggers/${id}/`, data);
    return response.data;
  },
  deleteAlertTrigger: async (id) => {
    await apiClient.delete(`/settings/alert-triggers/${id}/`);
  },

  // Idle Exemption Periods (Off-Working Time)
  getIdleExemptions: async (params) => {
    const response = await apiClient.get('/settings/idle-exemptions/', { params });
    return response.data;
  },
  createIdleExemption: async (data) => {
    const response = await apiClient.post('/settings/idle-exemptions/', data);
    return response.data;
  },
  updateIdleExemption: async (id, data) => {
    const response = await apiClient.patch(`/settings/idle-exemptions/${id}/`, data);
    return response.data;
  },
  deleteIdleExemption: async (id) => {
    await apiClient.delete(`/settings/idle-exemptions/${id}/`);
  },

  // Regenerate the organization API key (returns { api_key, api_key_created_at })
  regenerateApiKey: async () => {
    const response = await apiClient.post('/settings/tracking/regenerate-api-key/');
    return response.data;
  },

  // Telegram Integration
  getTelegramSettings: async () => {
    const response = await apiClient.get('/settings/telegram/');
    return response.data;
  },

  updateTelegramSettings: async (settings) => {
    const response = await apiClient.put('/settings/telegram/', settings);
    return response.data;
  },

  testTelegramNotification: async (payload = {}) => {
    const response = await apiClient.post('/settings/telegram/test/', payload);
    return response.data;
  },
};

export default settingsService;
